import { toast } from 'sonner';

/**
 * Optimistic-with-undo for reversible paired actions only (doc 09 §4.8): Ban↔Unban, Take-Down↔List,
 * Dismiss↔Re-review, Solve→Reopen. Each has a real inverse endpoint, so "Undo" is genuinely safe.
 * NEVER used for destructive (delete-comment / remove-*) — those get a blocking confirm with a required
 * reason and no undo path.
 */
export interface ReversibleOp {
  /** Success toast text. */
  label: string;
  /** Primary mutation. */
  forward: () => Promise<unknown>;
  /** Real inverse mutation (wired to the Undo action). */
  inverse: () => Promise<unknown>;
  /** Optional follow-up after the forward call (e.g. write a history entry). */
  afterForward?: () => Promise<unknown> | void;
  /** Invalidate / refetch — called after forward and after a successful undo. */
  onSettled?: () => void;
  /** Undo toast duration (ms). */
  undoDuration?: number;
}

export async function runReversible(op: ReversibleOp): Promise<void> {
  await op.forward();
  if (op.afterForward) await op.afterForward();
  op.onSettled?.();

  toast.success(op.label, {
    duration: op.undoDuration ?? 8000,
    action: {
      label: 'Undo',
      onClick: () => {
        void (async () => {
          try {
            await op.inverse();
            op.onSettled?.();
            toast.success('Action reverted');
          } catch {
            toast.error('Failed to undo');
          }
        })();
      },
    },
  });
}
